import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { map, orderBy, get, size, filter, forEach, groupBy, sumBy } from 'lodash';
import { FlatButton, FontIcon, IconButton, IconMenu, MenuItem, Paper, RaisedButton, Table, TableBody, TableHeader, TableHeaderColumn, TableRow, TableRowColumn } from 'material-ui';
import { grey500 } from 'material-ui/styles/colors';
import ContentFilter from 'material-ui/svg-icons/content/filter-list';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';

import { exportToExcel } from '../../utils/exportHelpers';
import { unitRender } from '../../utils/normalize';
import EntryModal from './components/EntryModal';
import pallet from '../../constants/colors';
import Alert from '../../components/Alert';
import { deleteEntry } from './actions';
import ExpectedHours from './components/ExpectedHours';
import MultiEntryModal from './components/MultiEntryModal';

const columnStyle = {
  whiteSpace: 'normal',
  width: 100,
};

const EntriesTable = ({
  entries, entryTypes, hideActions, deleteClockCardEntry, datesBetween,
}) => {
  const [dataSource, setDataSource] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [entriesByDate, setEntriesByDate] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [multiModalOpen, setMultiModalOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);

  const [selectedEntries, setSelectedEntries] = useState([]);
  const [selectedValue, setSelectedValue] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [initialDate, setInitialDate] = useState('');
  const selectedLanguage = localStorage.language === 'is' ? 'is' : 'en';

  const visibleColumns = [
    { key: 'Date', title: selectedLanguage === 'is' ? 'Dagsetning' : 'Date' },
    { key: 'ClockIn', title: selectedLanguage === 'is' ? 'Inn' : 'Clock In' },
    { key: 'ClockOut', title: selectedLanguage === 'is' ? 'Út' : 'Clock Out' },
    { key: 'ActualClockTime', title: selectedLanguage === 'is' ? 'Tímar' : 'Hours' },
    { key: 'ClockCardEntryTypeName', title: selectedLanguage === 'is' ? 'Tegund' : 'Type' },
    { key: 'ClockCardEntryComment', title: selectedLanguage === 'is' ? 'Athugasemd' : 'Comment' },
  ];

  useEffect(() => {
    setDataSource(orderBy(entries, ['ClockIn'], ['asc']));
    setFilteredData(orderBy(entries, ['ClockIn'], ['asc']));
    setEntriesByDate(groupBy(map(entries, entry => ({ ...entry, date: moment(entry.ClockIn).format('DD/MM/YY') })), 'date'));
    setSelectedValue(null);
  }, [entries]);

  const closeAlert = () => {
    setSelectedEntries([]);
    setAlertOpen(false);
  };
  const openAlert = () => {
    setAlertOpen(true);
  };

  const closeModal = () => {
    setInitialDate('');
    setSelectedEntries([]);
    setIsEdit(false);
    setModalOpen(false);
  };
  const openModal = () => {
    if (Array.isArray(selectedEntries[0])) {
      setMultiModalOpen(true);
    } else {
      setModalOpen(true);
    }
  };

  const onOpenModal = (entry, isEditing) => {
    isEditing && setIsEdit(true);
    setSelectedEntries(entry);
    if (size(entry) > 1) {
      setMultiModalOpen(true);
    } else {
      isEditing ? openModal() : openAlert();
    }
  };

  const createEntry = () => {
    setInitialDate(moment());
    setModalOpen(true);
  };

  const excelExport = () => {
    const documentName = `Tímaskráningar-${moment().format('DD.MM.YYYY')}`;
    const exportData = map(filteredData, item => ({
      Date: moment(item.ClockIn).format('DD.MM.YYYY'),
      ClockIn: moment(item.ClockIn).format('HH:mm'),
      ClockOut: item.ClockOut ? moment(item.ClockOut).format('HH:mm') : '',
      ActualClockTime: unitRender(item.ActualClockTime),
      ClockCardEntryTypeName: get(entryTypes, [
        item.ClockCardEntryTypeID,
        selectedLanguage === 'is' ? 'ClockCardEntryTypeName' : 'ClockCardEntryTypeName_EN',
      ]),
      ClockCardEntryComment: item.ClockCardEntryComment,
    }));
    exportToExcel(documentName, exportData, visibleColumns);
  };

  const closeAndDelete = () => {
    forEach(selectedEntries, (entry) => {
      deleteClockCardEntry(get(entry, 'ClockCardEntryID'));
    });
    setAlertOpen(false);
    setSelectedEntries([]);
  };

  const isExportDisabled = () => filteredData.length === 0;

  const onFilterChange = (value) => {
    if (value) {
      const filtered = filter(dataSource, ['ClockCardEntryTypeID', value]);
      setEntriesByDate(groupBy(map(filtered, entry => ({ ...entry, date: moment(entry.ClockIn).format('DD/MM/YY') })), 'date'));
      setFilteredData(filtered);
      setSelectedValue(value);
    } else {
      setFilteredData(dataSource);
      setEntriesByDate(groupBy(map(dataSource, entry => ({ ...entry, date: moment(entry.ClockIn).format('DD/MM/YY') })), 'date'));
      setSelectedValue(null);
    }
  };

  const onMultipleEntrySelect = (entry) => {
    setMultiModalOpen(false);
    setSelectedEntries([entry]);
    openModal();
  };
  const onMultipleEntryDelete = (entry) => {
    setMultiModalOpen(false);
    setSelectedEntries([entry]);
    openAlert();
  };
  const closeMultiModal = () => {
    setIsEdit(false);
    setMultiModalOpen(false);
    setSelectedEntries([]);
  };
  const createFromDate = (date) => {
    const [day, month, year] = date.split('/');
    const customDate = new Date(`20${year}`, month - 1, day, 0, 0, 0);
    setInitialDate(moment(customDate));
    openModal();
  };
  return (
    <React.Fragment>
      <Alert
        open={alertOpen}
        onOk={closeAndDelete}
        onCancel={closeAlert}
        text={<FormattedMessage id="time.alert.delete" values={{ count: size(selectedEntries.flat()) }} />}
      />
      <EntryModal
        modalState={modalOpen}
        closeModal={closeModal}
        selectedEntries={selectedEntries}
        entriesByDate={entriesByDate}
        initialDate={initialDate}
        isEdit={isEdit}
      />
      {multiModalOpen && <MultiEntryModal
        modalState={multiModalOpen}
        isEdit={isEdit}
        closeModal={closeMultiModal}
        selectedEntries={selectedEntries}
        onMultipleEntrySelect={onMultipleEntrySelect}
        onMultipleEntryDelete={onMultipleEntryDelete}
        selectedLanguage={selectedLanguage}
      />}
      <Paper style={{ padding: 24, maxWidth: 1420, margin: 'auto' }}>
        <div
          style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between',
          }}
        >
          {!hideActions && <RaisedButton
            primary
            label={<FormattedMessage id="time.CreateEntry" />}
            onClick={createEntry}
            disabled={size(selectedEntries) > 0}
            icon={<FontIcon className="material-icons">add</FontIcon>}
          />}
          <div className="hide-mobile"><ExpectedHours /></div>
          {!hideActions && <RaisedButton
            primary
            label={<FormattedMessage id="editPanel.exportExcel" />}
            onClick={excelExport}
            icon={<FontIcon className="material-icons">file_download</FontIcon>}
            disabled={isExportDisabled()}
          />}
        </div>
        <div className="show-mobile"><ExpectedHours /></div>
        <Table
          bodyStyle={{ overflowX: 'none', overflowY: 'none' }}
          height="calc(100vh - 320px)"
          // fixedHeader
          multiSelectable={false}
          selectable={false}
        >
          <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
            <TableRow>
              <TableHeaderColumn style={{ width: 110 }}><FormattedMessage id="time.Date" /></TableHeaderColumn>
              <TableHeaderColumn style={columnStyle}><FormattedMessage id="time.ClockIn" /></TableHeaderColumn>
              <TableHeaderColumn style={columnStyle}><FormattedMessage id="time.ClockOut" /></TableHeaderColumn>
              <TableHeaderColumn style={columnStyle}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <FormattedMessage id="time.Type" />
                  <IconMenu
                    iconButtonElement={<IconButton><ContentFilter color={selectedValue === null ? grey500 : pallet.primary1Color} /></IconButton>}
                    value={selectedValue || null}
                  >
                    <MenuItem
                      key="all"
                      value={null}
                      primaryText={<FormattedMessage id="repository.all" />}
                      onClick={() => onFilterChange(null)}
                    />
                    {map(entryTypes, ({
                      ClockCardEntryTypeID, ClockCardEntryTypeName,
                    }) => (
                      <MenuItem
                        key={ClockCardEntryTypeID}
                        value={ClockCardEntryTypeID}
                        primaryText={ClockCardEntryTypeName}
                        onClick={() => onFilterChange(ClockCardEntryTypeID)}
                      />
                    ))}
                  </IconMenu>
                </div>
              </TableHeaderColumn>
              <TableHeaderColumn style={columnStyle}><FormattedMessage id="time.ActualClockTime" /></TableHeaderColumn>
              <TableHeaderColumn style={columnStyle}><FormattedMessage id="time.Comment" /></TableHeaderColumn>
              <TableHeaderColumn style={{ width: 48 }}><FormattedMessage id="time.HasError" /></TableHeaderColumn>
              <TableHeaderColumn style={columnStyle}><FormattedMessage id="panel.actions" /></TableHeaderColumn>
            </TableRow>
          </TableHeader>
          <TableBody
            displayRowCheckbox={false}
            deselectOnClickaway={false}
          >
            {map(datesBetween, ({
              dateString, isWeekend, isRedDay, dayOfWeek, date
            }) => (
              <TableRow
                key={dateString}
                style={{ backgroundColor: (isWeekend || isRedDay) ? '#3b53a70d' : 'white' }}
              >
                <TableRowColumn style={{ width: 110 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ textAlign: 'center', width: 20 }}>{dayOfWeek}</span>
                    <span>{date.format('DD.MM.YY')}</span>
                  </div>
                </TableRowColumn>
                <TableRowColumn style={columnStyle}>
                  {map(get(entriesByDate, dateString), entry => (
                    <div key={entry.ClockCardEntryID}>{moment(entry.ClockIn).format('HH:mm')}</div>
                  ))}
                </TableRowColumn>
                <TableRowColumn style={columnStyle}>
                  {map(get(entriesByDate, dateString), entry => (
                    <div key={entry.ClockCardEntryID}>{entry.ClockOut ? moment(entry.ClockOut).format('HH:mm') : ''}</div>
                  ))}
                </TableRowColumn>
                <TableRowColumn style={columnStyle}>
                  {map(get(entriesByDate, dateString), entry => (
                    <div key={entry.ClockCardEntryID}>{get(entryTypes, [entry.ClockCardEntryTypeID, selectedLanguage === 'is' ? 'ClockCardEntryTypeName' : 'ClockCardEntryTypeName_EN'])}</div>
                  ))}
                </TableRowColumn>
                <TableRowColumn style={columnStyle}>
                  {unitRender(sumBy(get(entriesByDate, dateString), 'ActualClockTime')) || ''}
                </TableRowColumn>
                <TableRowColumn style={{ width: 100, overflow: 'visible' }}>
                  {map(get(entriesByDate, dateString), entry => (
                    get(entry, 'ClockCardEntryComment') ? (
                      <div className="tooltip">
                        <div key={entry.ClockCardEntryID} style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
                          {get(entry, 'ClockCardEntryComment') || <br />}
                        </div>
                        <span className="tooltiptext">{get(entry, 'ClockCardEntryComment') || <br />}</span>
                      </div>
                    ) : <br />
                  ))}
                </TableRowColumn>
                <TableRowColumn style={{ width: 48, overflow: 'visible' }}>
                  {map(get(entriesByDate, dateString), entry => (
                    get(entry, 'ClockTimeHasError') ? (
                      <div>
                        <IconButton
                          disabled
                          style={{ padding: 0, height: 12 }}
                          tooltip={<div><FormattedMessage id="time.HasError.Tooltip" /></div>}
                          iconStyle={{ color: pallet.warningColor, padding: 0, height: 12 }}
                          iconClassName="material-icons"
                          color={pallet.warningColor}
                        >
                          report_problem
                        </IconButton>
                      </div>
                    ) : ''
                  ))}
                </TableRowColumn>
                <TableRowColumn style={columnStyle}>
                  {get(entriesByDate, dateString) ? (
                    <div>
                      <IconButton
                        iconStyle={{ color: pallet.primary1Color }}
                        onClick={() => onOpenModal(get(entriesByDate, dateString), true)}
                      >
                        <FontIcon className="material-icons">mode</FontIcon>
                      </IconButton>
                      <IconButton
                        iconStyle={{ color: pallet.errorColor }}
                        onClick={() => onOpenModal(get(entriesByDate, dateString), false)}
                      >
                        <FontIcon className="material-icons">delete_outlined</FontIcon>
                      </IconButton>
                    </div>
                  ) : (
                    <FlatButton
                      primary
                      label={<FormattedMessage id="time.CreateEntry" />}
                      onClick={() => createFromDate(dateString)}
                    />
                  )}
                </TableRowColumn>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </React.Fragment>
  );
};

EntriesTable.propTypes = {
  deleteClockCardEntry: PropTypes.func.isRequired,
  entries: PropTypes.shape({}),
  entryTypes: PropTypes.shape({}).isRequired,
  start: PropTypes.shape({}).isRequired,
  hideActions: PropTypes.bool.isRequired,
  datesBetween: PropTypes.arrayOf(PropTypes.shape({})),
};

export default connect(
  ({ timeReducer }) => ({ ...timeReducer }),
  dispatch => ({
    deleteClockCardEntry: id => dispatch(deleteEntry(id)),
  }),
)(EntriesTable);



// WEBPACK FOOTER //
// ./src/sites/time/EntriesTable.js